import 'react-quill/dist/quill.snow.css';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  editor: {
    '& .ql-container': {
      minHeight: 250,
      borderBottomRightRadius: 8,
      borderBottomLeftRadius: 8,
      fontSize: 16,
    },
    '& .ql-toolbar': {
      background: 'white',
      borderBottom: 'none !important',
      borderTopRightRadius: 8,
      borderTopLeftRadius: 8,
    },
  },
}));

// Very safe HTML cleaner (removes everything dangerous + fixes broken tags)
const cleanHtml = (html = '') => {
  if (!html || typeof html !== 'string') return '<p></p>';

  // Remove script, style, and dangerous attributes
  let clean = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/on\w+\s*=\s*['"][^'"]*['"]/gi, '')
    .replace(/javascript:/gi, '');

  // Wrap naked text in <p> and ensure root is not <div>
  if (!/^<(div|p|br)/i.test(clean.trim())) {
    clean = `<p>${clean}</p>`;
  }

  // Replace multiple <br> or empty tags
  clean = clean.replace(/<p>\s*<\/p>/g, '<p><br></p>');
  clean = clean.replace(/<p>&nbsp;<\/p>/g, '<p><br></p>');

  return clean || '<p><br></p>';
};

const RichTextEditor = ({ content, placeholder, handleContentChange }) => {
  const classes = useStyles();
  const quillRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  // Delay mounting to prevent SSR issues + give Quill time to initialize
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Safely inject HTML when content changes
  useEffect(() => {
    if (!quillRef.current || !isReady) return;

    const editor = quillRef.current.getEditor();
    if (!editor) return;

    const cleaned = cleanHtml(content);

    // Prevent unnecessary re-render + cursor jump
    const currentHtml = editor.root.innerHTML;
    if (currentHtml === cleaned || currentHtml === '<p><br></p>' && cleaned === '<p></p>') {
      return;
    }

    try {
      editor.root.innerHTML = cleaned;
      editor.clipboard.convert(cleaned); // Rebuild internal Delta
    } catch (err) {
      console.warn('Quill failed to set content, falling back to text:', err);
      editor.setText(content?.replace(/<[^>]*>/g, '') || '');
    }
  }, [content, isReady]);

  if (!isReady) {
    return <div style={{ minHeight: 300, background: '#fff', borderRadius: 8 }} />;
  }

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      [{ color: [] }, { background: [] }],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block', 'list', 'bullet', 'indent', 'align',
    'color', 'background', 'link', 'image', 'video'
  ];

  return (
    <ReactQuill
      ref={quillRef}
      className={classes.editor}
      theme="snow"
      placeholder={placeholder || 'Start writing...'}
      modules={modules}
      formats={formats}
      onChange={(html) => {
        if (handleContentChange) handleContentChange(html);
      }}
      // DO NOT bind value={content} directly → this is the main cause of crashes!
      // We control content manually via useEffect above
    />
  );
};

RichTextEditor.propTypes = {
  content: PropTypes.string,
  placeholder: PropTypes.string,
  handleContentChange: PropTypes.func.isRequired,
};

RichTextEditor.defaultProps = {
  content: '',
  placeholder: '',
};

export default RichTextEditor;